<template>
  <div class="activity-stage">
    <assessment-helper></assessment-helper>
    <div class="mat-man-match">
      <div class="drag-completed" v-if="showImage">
        <span class="image-header">{{ currentRound.shape }}</span>
        <img alt class="completed-image" :src="currentRound.completed" />
      </div>
      <div class="drag-panel" v-if="!showImage">
        <div class="drag-container">
          <img alt id="dragging-area" :src="currentRound.empty" />
        </div>
        <div class="draggable-items" :class="{ isMobile }">
          <div
            class="draggable-item"
            v-for="(draggableShape, index) in currentRound.draggableShapes"
            :key="index"
          >
            <div class="image-container">
              <img
                alt
                :id="draggableShape.id"
                class="selected-image"
                :src="draggableShape.selected"
              />
              <img alt class="unselected-image" :src="draggableShape.unselected" />
            </div>
            <div class="image-text">{{ draggableShape.shape }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TweenMax, Power0 } from 'gsap';
import Draggable from 'gsap/Draggable';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';
import { createNamespacedHelpers } from 'vuex';

import AssessmentHelper from 'components/utils/AssessmentHelper.vue';

const application = createNamespacedHelpers('application');

export default {
  components: { AssessmentHelper },

  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger],

  data() {
    return {
      showImage: false,
      sounds: null
    };
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    })
  },

  methods: {
    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages(activityState);
      this.sounds = await this.loadSounds(activityState);
    },

    async loadSounds(activityState) {
      const { availableShapes } = activityState;
      const sounds = (availableShapes || []).map(({ audio, shape }) => ({
        audio,
        shape
      }));

      this.queueAudio(sounds.map(sound => sound.audio));
      const cache = await this.downloadAudio();
      return sounds.map(sound => ({
        shape: sound.shape,
        howler: cache.get(sound.audio)
      }));
    },

    async loadImages(activityState) {
      const { rounds } = activityState;

      rounds.forEach(({ draggableShapes, empty, completed, selected, unselected }) => {
        this.queueImage([empty, completed, selected, unselected]);
        draggableShapes.forEach(shape => this.queueImage([shape.selected, shape.unselected]));
      });

      await this.downloadImages();
    },

    getActivityState() {
      const { rounds, availableShapes } = this.activityData;

      return {
        rounds,
        availableShapes,
        instructions: {
          delay: 5,
          written: "Move the shape to match Mat Man's shape.",
          voice: `${this.voiceInstructionsPrefix}/matmanmatch-fa-1.mp3`
        }
      };
    },

    startActivity() {
      this.hideKeyboard();
      this.errorLoggerStart();
      this.setupNewRound();
      TweenMax.delayedCall(1, () => {
        TweenMax.to('.drag-panel', 2, { opacity: 1 });
        this.handleRoundStart();
      });
    },

    getVerticalDropThreshold(matManImageEl) {
      return (60 / 100) * (matManImageEl.height / 3);
    },

    handleDrop(context) {
      const self = this;
      const {
        target,
        vars: { shape }
      } = context;
      const draggingAreaDOM = document.getElementById('dragging-area');
      const dragTargetCoordinates = target.getBoundingClientRect();
      const draggingDOMRect = draggingAreaDOM.getBoundingClientRect();
      const verticalThreshold = this.getVerticalDropThreshold(draggingAreaDOM);

      const isCorrectDrag =
        draggingDOMRect.top + verticalThreshold < dragTargetCoordinates.top &&
        draggingDOMRect.bottom - verticalThreshold > dragTargetCoordinates.bottom &&
        draggingDOMRect.left < dragTargetCoordinates.left &&
        draggingDOMRect.right > dragTargetCoordinates.right &&
        shape === this.currentRound.shape;

      if (isCorrectDrag) {
        this.sndBell();
        this.logCorrectCharacter();
        this.sounds.forEach(sound => {
          if (sound.shape === this.currentRound.shape) {
            sound.howler.play();
          }
        });

        TweenMax.to('.drag-panel', 0.2, {
          opacity: 0,
          onComplete() {
            self.showImage = true;
            self.$nextTick(() =>
              TweenMax.to('.drag-completed', 0.3, {
                opacity: 1,
                onComplete() {
                  TweenMax.delayedCall(4, () => {
                    if (self.isLastRound) {
                      self.completeActivity();
                      return;
                    }

                    TweenMax.to('.drag-completed', 1, {
                      opacity: 0,
                      onComplete() {
                        self.showImage = false;
                        self.$nextTick(() => {
                          self.setupNewRound();
                          self.$nextTick(() => self.handleRoundStart(true));
                        });
                      }
                    });
                  });
                }
              })
            );
          }
        });
      } else {
        this.sndError();
        this.logError(shape, this.currentRound.shape, 'DNDplace');
        TweenMax.to(target, 0.5, { x: '-50%', y: 0, ease: Power0.easeNone });
      }
    },

    handleRoundStart(isNewRound = false) {
      const self = this;

      if (isNewRound) {
        self.createDraggables();
        TweenMax.to('.drag-panel', 1, { opacity: 1 });
      } else {
        // first round
        TweenMax.to('.drag-panel', 0.5, {
          opacity: 1,
          onComplete() {
            self.createDraggables();
          }
        });
      }
    },

    createDraggables() {
      let bounds = null;
      const self = this;
      const { isMobile } = this.userDevice;

      this.currentRound.draggableShapes.forEach(draggableShape => {
        let shapeTimer = 0;
        let hasHitBoundary = false;
        Draggable.create(`#${draggableShape.id}`, {
          cursor: 'grab',
          shape: draggableShape.shape,
          onPress() {
            if (bounds) return;
            // get DOMRect bounding
            bounds = getActivityBoundings();
          },
          onDrag() {
            if (self.timerActive) {
              self.resetTimer();
            }
            // work-around for iOS not firing touchend on tablet
            if (!isMobile) return;
            clearTimeout(shapeTimer);
            const { pointerX, pointerY } = this;
            const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds);
            if (hasTouched && !hasHitBoundary) {
              shapeTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDrop(this);
              }, 200);
            }
          },
          onDragStart() {
            TweenMax.set(`#${draggableShape.id}`, { cursor: 'grabbing' });
          },
          onDragEnd() {
            clearTimeout(shapeTimer);
            hasHitBoundary = false;
            self.handleDrop(this);
          }
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

#dragging-area {
  display: block;
  margin: 0 auto;
  height: auto;
  width: 300px;
}

.image-header {
  font-size: 35px;
  margin-top: 20px;
  margin-bottom: 10px;
  font-family: hwtfutura-bold;
  text-transform: uppercase;
  text-align: center;
  display: block;
}

.drag-panel {
  opacity: 0;
}

.drag-completed {
  opacity: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .completed-image {
    height: auto;
    margin: 0 auto;
    display: block;
    max-width: 480px;
  }
}

.mat-man-match {
  height: 100%;
  box-sizing: border-box;
}

.draggable-items {
  width: 100%;
  display: flex;
  padding: 30px 15px;
  box-sizing: border-box;

  .draggable-item {
    width: 25%;
    height: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
}

.draggable-items.isMobile {
  padding: 0px 15px !important;
}

.selected-image,
.unselected-image {
  left: 50%;
  transform: translateX(-50%);
}

.image-container {
  position: relative;

  .selected-image {
    position: relative;
    z-index: 150;
  }
  .unselected-image {
    position: absolute;
    z-index: 100;
  }
}

.image-text {
  margin-top: 2px;
  font-family: hwtfutura-bold;
  text-transform: uppercase;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
}
</style>
